import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCartContext } from '../../context/CartContext';
import { GlobalContext } from '../../context/GlobalContext';
import { formatDateTime } from '../../utils/DateUtils';
import Button from './Button';

const CartDrawer = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { store, goToStep, formatAmounts } = useContext(GlobalContext);
  const {
    cart,
    totalAmounts,
    totalFinal,
    deleteRequestCartItem,
    promoCode,
    promoDiscount,
    promoError,
    promoLoading,
    applyPromoCode,
  } = useCartContext();

  const [promoInput, setPromoInput] = useState(promoCode || '');
  const [promoOpen, setPromoOpen] = useState(!!promoCode);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const drawerRef = useRef(null);
  const previousFocusRef = useRef(null);

  const items = cart.items || [];
  const cartCount = items.length;
  const reduction = totalAmounts - totalFinal;

  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current = document.activeElement;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && drawerRef.current) {
        const focusables = drawerRef.current.querySelectorAll(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    if (drawerRef.current) {
      const focusables = drawerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length > 0) focusables[0].focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
      if (previousFocusRef.current) previousFocusRef.current.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const goHomeIfNeeded = () => {
    if (!window.location.pathname.includes('/home')) {
      window.location.href = '/home';
    }
  };

  const handleBuyCard = () => {
    onClose();
    goHomeIfNeeded();
    goToStep(1);
  };

  const handleOrder = () => {
    onClose();
    goHomeIfNeeded();
    goToStep(2);
  };

  const handleApplyPromo = () => {
    if (promoInput.trim()) applyPromoCode(promoInput.trim());
  };

  return (
    <div
      className="cartDrawerOverlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        id="cartDrawer"
        className="cartDrawer"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-drawer-title"
        tabIndex="-1"
      >
        <div className="cartDrawerHead">
          <h2 id="cart-drawer-title">{t('Panier')} ({cartCount})</h2>
          <button
            type="button"
            className="cartDrawerClose"
            onClick={onClose}
            aria-label={t('Fermer')}
          >
            &times;
          </button>
        </div>

        {cartCount === 0 ? (
          <div className="cartDrawerBody">
            <div className="rfEmptyCart">
              <div className="emtyCartHead">
                <i className="icon-panier" aria-hidden="true"></i>
              </div>
              <p>{t('Votre panier est vide, mais vous pouvez changer cela en offrant une e-carte cadeau !')}</p>
              <Button className="sendOrder" onClick={handleBuyCard}>
                {t('Acheter une e-carte cadeau')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="cartDrawerBody">
              {items.map((item, index) => (
                <div key={index} className="rfCartItem">
                  <div className="itemPreviewBox">
                    <img
                      className="itemPreview"
                      src={item.card || 'placeholder.jpg'}
                      alt={t('Aperçu de la carte cadeau')}
                    />
                  </div>
                  <div className="itemData">
                    {(item.firstNameReceiver || item.lastNameReceiver) && (
                      <div className="rfSender typoBold">
                        {item.firstNameReceiver} {item.lastNameReceiver}
                      </div>
                    )}
                    {item.emailReceiver && (
                      <div className="rfSender">{item.emailReceiver}</div>
                    )}
                    {item.message && (
                      <div className="cardComment">{item.message}</div>
                    )}
                    {item.momentSend === 'after' && item.sendDate && (
                      <div className="rfDate sendBadge">
                        {t('Envoi le')} {formatDateTime.formatDisplayDate(item.sendDate, store.format_date)} {t('à')} {formatDateTime.formatDisplayTime(item.sendDate, store.format_time).time}
                      </div>
                    )}
                  </div>
                  <div className="itemSide">
                    <button
                      type="button"
                      className="deleteItem"
                      onClick={() => setDeleteIndex(index)}
                      aria-label={t('Supprimer cet article')}
                    >
                      &times;
                    </button>
                    <div className="rfPrice typoBold">
                      {formatAmounts(item.amount, item.amount % 1 ? 2 : 0)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="cartDrawerTotals">
              <div className="totalRow subTotal">
                <span>{t('Sous-total')}</span>
                <span>{formatAmounts(totalAmounts, 2)}</span>
              </div>

              <div className="promoBox">
                <button
                  type="button"
                  className="promoToggle"
                  aria-expanded={promoOpen}
                  aria-controls="drawerPromoRow"
                  onClick={() => setPromoOpen((open) => !open)}
                >
                  {!promoOpen && (
                    <span className="promoTogglePlus" aria-hidden="true">+</span>
                  )}
                  {t('Vous avez un code promo ?')}
                  {promoOpen && (
                    <span className="promoTogglePlus" aria-hidden="true">&minus;</span>
                  )}
                </button>
                {promoOpen && (
                  <div className="promoRow" id="drawerPromoRow">
                    <div className="promoField">
                      <input
                        id="drawerPromoCode"
                        type="text"
                        placeholder={t('Code promo')}
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleApplyPromo();
                        }}
                      />
                      <label className="flLabel" htmlFor="drawerPromoCode" aria-hidden="true">
                        {t('Code promo')}
                      </label>
                    </div>
                    <Button
                      variant="outline"
                      isLoading={promoLoading}
                      disabled={!promoInput.trim() || promoInput.trim() === promoCode}
                      onClick={handleApplyPromo}
                    >
                      {t('Appliquer')}
                    </Button>
                  </div>
                )}
                {promoError && (
                  <div className="field-error" role="alert">{t(promoError)}</div>
                )}
                {promoDiscount && reduction > 0 && (
                  <div className="rfDiscount discountRow">
                    <span>{t('Réduction')}</span>
                    <span>-{formatAmounts(reduction, 2)}</span>
                  </div>
                )}
              </div>

              <div className="totalRow grandTotal">
                <span>{t('Total')}</span>
                <span>{formatAmounts(totalFinal, 2)}</span>
              </div>
            </div>

            <div className="cartDrawerFooter">
              <Button variant="outline" className="buyOther" onClick={handleBuyCard}>
                {t('Acheter une autre e-carte cadeau')}
              </Button>
              <Button className="sendOrder" onClick={handleOrder}>
                {t('Commander')} - {formatAmounts(totalFinal, 2)}
              </Button>
            </div>

            {deleteIndex !== null && (
              <div
                className="cartDrawerDeleteConfirm"
                role="alertdialog"
                aria-labelledby="drawer-delete-title"
                aria-describedby="drawer-delete-desc"
              >
                <div className="deleteConfirmBox">
                  <i className="icon-trash" aria-hidden="true"></i>
                  <p id="drawer-delete-title" className="typoBold">{t('Confirmation de suppression')}</p>
                  <p id="drawer-delete-desc">{t('Êtes-vous sûr de vouloir supprimer la carte ?')}</p>
                  <div className="deleteConfirmBtns">
                    <Button className="sendOrder" onClick={() => {
                      deleteRequestCartItem(deleteIndex);
                      setDeleteIndex(null);
                    }}>
                      {t('Supprimer')}
                    </Button>
                    <Button variant="outline" className="buyOther" onClick={() => setDeleteIndex(null)}>
                      {t('Non')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
